import { v2Client } from '@api/apiClient'
import { Input } from '@components/chat/Input'
import { Message } from '@components/chat/Message'
import { MessageList } from '@components/chat/MessageList'
import { isSystemMessage, MessageType, UserMessage } from '@components/chat/types'
import { extensions, Filters, sendQuerySSERequest } from '@components/utils/sendQuery'
import { useGetConversation } from '@features/conversation/hooks/useGetConversation'
import { conversationsKeys } from '@features/conversation/hooks/useGetConversations'
import { indexStatuses, useGetIndexStatus } from '@features/indexing/hooks/useGetIndexStatus'
import Box from '@mui/material/Box'
import { useQueryClient } from '@tanstack/react-query'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'

type Props = {
  chatId?: string
  setChatId?: Dispatch<SetStateAction<string | undefined>>
  messages: MessageType[]
  setMessages: Dispatch<SetStateAction<Array<MessageType>>>
}

export const Conversation = ({ chatId, setChatId, messages, setMessages }: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const [filters, setFilters] = useState<Filters>({ language: 'pl', extension_filter: [], strategy: 'e5' })
  const abortControllerRef = useRef<AbortController | null>(null)
  const taskIdRef = useRef<string | null>(null)
  const queryClient = useQueryClient()
  const { data, isError } = useGetConversation(chatId)
  const firstMessage = data?.messages?.find((message) => message.type === 'system')
  const { data: statusData } = useGetIndexStatus()

  useEffect(() => {
    if (firstMessage) {
      const extension_filter: string[] = []
      Object.entries(extensions).forEach(([key, value]) => {
        if (!extension_filter.includes(key) && value.some((v) => firstMessage.extension_filter?.includes(v))) {
          extension_filter.push(key)
        }
      })
      setFilters({
        language: firstMessage?.language || 'en',
        extension_filter,
        strategy: firstMessage?.strategy || 'e5',
      })
    }
  }, [firstMessage])

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      taskIdRef.current = null
    }
  }, [])

  const handleSend = async (message: UserMessage) => {
    setIsLoading(true)
    try {
      const { controller, taskIdRef: taskIdRefFromSSE } = sendQuerySSERequest({
        query: message.query,
        files: message.files,
        setMessages,
        onCreatedChat: (newChatId) => {
          console.log('Created new chat with ID:', newChatId)
          if (setChatId) {
            setChatId(newChatId)
          }
          queryClient.invalidateQueries({ queryKey: conversationsKeys.getAll })
        },
        chat: chatId,
        setLoader: setIsLoading,
        filters,
      })
      abortControllerRef.current = controller

      // Monitor taskId changes
      const checkTaskId = setInterval(() => {
        if (taskIdRefFromSSE.current && taskIdRefFromSSE.current !== taskIdRef.current) {
          taskIdRef.current = taskIdRefFromSSE.current
          console.log('[handleSend] Updated taskId:', taskIdRef.current)
        }
      }, 100)

      // Clear interval when component unmounts or when loading stops
      const cleanup = () => {
        clearInterval(checkTaskId)
      }

      // Store cleanup function (we'll call it in handleStop or when loading finishes)
      setTimeout(() => {
        if (!isLoading) {
          cleanup()
        }
      }, 5000) // Fallback cleanup after 5 seconds
    } catch (error) {
      setIsLoading(false)
      console.error('Error sending message:', error)
    }
  }

  const handleStop = async () => {
    console.log('[handleStop] Called, abortControllerRef.current:', abortControllerRef.current)
    console.log('[handleStop] taskIdRef.current:', taskIdRef.current)

    // Call stop API if we have a task_id
    if (taskIdRef.current) {
      try {
        console.log('[handleStop] Calling stop API for task:', taskIdRef.current)
        await v2Client.post(`/chat-messages/${taskIdRef.current}/stop`, {
          method: 'POST',
        })
        console.log('[handleStop] Stop API call successful')
      } catch (error) {
        console.error('[handleStop] Error calling stop API:', error)
      }
    }

    // Abort the fetch request
    if (abortControllerRef.current) {
      console.log('[handleStop] Aborting request')
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }

    // Clear task ID and stop loading
    taskIdRef.current = null
    setIsLoading(false)
  }

  return (
    <Box
      display="grid"
      justifyContent="center"
      justifyItems="center"
      gridTemplateRows="1fr auto"
      gridTemplateColumns="1fr"
      height="calc(100% + 64px)"
      width="calc(100% + 64px)"
      m={-4} //remove layout padding
    >
      {!isError ? (
        <>
          <MessageList
            messages={messages}
            isLoading={isLoading}
          />
          <Box
            width="77%"
            pb={4}
          >
            <Input
              addMessage={(message) => {
                setMessages((previous) => [...previous, message])
                if (!isSystemMessage(message)) {
                  void handleSend(message)
                }
              }}
              setFilters={setFilters}
              filters={filters}
              chatId={chatId}
              disabled={
                !statusData?.status || ![indexStatuses.READY, indexStatuses.STOPPED].includes(statusData?.status)
              }
              isLoading={isLoading}
              onStop={handleStop}
            />
          </Box>
        </>
      ) : (
        <Message
          position={'left'}
          message={'Error fetching conversation'}
        />
      )}
    </Box>
  )
}
